<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { AxiosError } from 'axios'
import { Dayjs } from 'dayjs'
import { BannerCode, type Banner, useUserStore } from '@store'

definePageMeta({
  layout: 'one-column',
})

useSetSEO()

// const config = useRuntimeConfig()
const { showSignInDialog } = useMembershipProcess()
const { gtEvent } = useGoogleTag()
const { t, messages, locale } = useI18n()
const attendanceStore = useAttendanceStore()
const etcStore = useEtcStore()
const userStore = useUserStore()
const { dayjs } = useDayjs()
const { handleOnUpdateNotificationService } = useUpdateNotification()
const initDay = ref(dayjs())
const swiperOptions = {
  navigation: true,
  pagination: {
    clickable: true,
  },
  modules: [Navigation, Pagination, Autoplay],
}
const banner = ref<Banner>()
const dailyCheckLoading = ref(false)
const dailyCheckNotificationLoading = ref(false)
const dailCheckListLoading = ref(false)
// const guide = computed(() =>
//   t('dailyCheck.guide', { appName: config.public.APP_NAME })
// )
const guides = computed(
  () =>
    (messages.value[locale.value].dailyCheck as any).guides?.map(
      (_item: string, index: number) => t(`dailyCheck.guides[${index}]`)
    ) || []
)
const err = ref<AxiosError>()

onBeforeMount(async () => {
  const banners = await etcStore.fetchBanners({
    bannerSlotCode: BannerCode.DAILY_CHECK,
  })
  banner.value = banners[0]

  if (userStore.isSignIn) {
    await attendanceStore.fetchDailyCheck({
      date: initDay.value.format('YYYY-MM'),
    })
  }
})

/**
 * 출석 체크
 */
const handleOnDailyCheck = async () => {
  if (!userStore.isSignIn) {
    await showSignInDialog()
    return
  }

  if (attendanceStore.dailyCheck.hasAttendanceToday) return

  try {
    dailyCheckLoading.value = true
    await attendanceStore.reqDailyCheck()
    await handleOnUpdateDay(dayjs())
    gtEvent('userAction', {
      eventCategory: '사용자',
      eventAction: '출석 체크',
      eventLabel: 'daily-check-button',
      eventSlot: '',
      eventI18nAddr: '',
      eventComponent: 'Button',
    })
    useToast(t('dailyCheck.reqDailyCheck.success'))
  } catch (err) {
    if (err instanceof AxiosError) {
      switch (err.response?.status) {
        case 2010: {
          useToast(t(`dailyCheck.reqDailyCheck.errors.${err.response.status}`))
          break
        }
        default: {
          useToast(t('commonError.network.message'))
          break
        }
      }
    }
  } finally {
    dailyCheckLoading.value = false
  }
}

const handleOnUpdateDailyCheckNotification = async (value: boolean) => {
  if (!userStore.isSignIn) {
    await showSignInDialog()
    return
  }

  const loader = () => {
    dailyCheckNotificationLoading.value = !dailyCheckNotificationLoading.value
  }

  try {
    dailyCheckNotificationLoading.value = true
    await handleOnUpdateNotificationService(
      {
        ntcnSetupId: attendanceStore.dailyCheck.ntcnSetupId,
        svcNtcnRecptnSetupAt: value ? 'Y' : 'N',
      },
      loader
    )
    gtEvent('selectEvent', {
      eventCategory: '선택',
      eventAction: `출석 체크 알림 받기 > ${value ? '켜기' : '끄기'}`,
      eventLabel: 'close-extend',
      eventSlot: 'Modal',
      eventI18nAddr: 'close-extend',
      eventComponent: 'Button',
    })
    // attendanceStore.dailyCheck.notification = value
  } finally {
    dailyCheckNotificationLoading.value = false
  }
}

const fetchDailyCheckError = (
  error: AxiosError,
  type: 'ssr' | 'csr' | 'toast'
) => {
  switch (error.response?.status) {
    case 2006:
    case 9999:
    default:
      switch (type) {
        // case 'ssr':
        //   throw createError({
        //     statusCode: 403,
        //     statusMessage: 'daily-check access require login',
        //   })
        // case 'csr':
        //   pageError.value = createError({
        //     statusCode: 403,
        //     statusMessage: 'daily-check access require login',
        //   })
        //   break
        case 'toast':
          useToast(t('commonError.network.message'))
      }
      break
  }
}

const handleOnUpdateDay = async (day: Dayjs) => {
  if (!userStore.isSignIn) return

  initDay.value = day
  try {
    dailCheckListLoading.value = true
    await attendanceStore.fetchDailyCheck({ date: day.format('YYYY-MM') })
  } catch (err) {
    if (err instanceof AxiosError) {
      fetchDailyCheckError(err, 'toast')
    }
  } finally {
    dailCheckListLoading.value = false
  }
}

const handleOnGoBannerLink = () => {
  if (banner.value?.bannerLinkAt === 'Y') {
    useNavigations({
      external: banner.value!.bannerExtrlLinkAt === 'Y',
      url: banner.value!.bannerLinkUrl,
    })
  }
}

/**
 * SSR
 */
// const { error: pageError } = await useAsyncData('dailyCheck', async () => {
//   try {
//     await attendanceStore.fetchDailyCheck({
//       date: initDay.value.format('YYYY-MM'),
//     })
//   } catch (_err) {
//     err.value = _err as AxiosError
//     return Promise.reject(_err)
//   }
// })
//
// if (err.value && err.value instanceof AxiosError) {
//   fetchDailyCheckError(err.value, process.server ? 'ssr' : 'csr')
// }
</script>

<template>
  <div v-if="!err" class="page-daily-check">
    <div class="page-title-wrap">
      <h4 class="page-title">{{ t('dailyCheck.dailyCheckin') }}</h4>
    </div>

    <div class="page-contents-wrap">
      <div class="section-row">
        <div
          v-if="banner"
          :class="['banner-wrap', { link: banner.bannerLinkAt === 'Y' }]"
          @click="handleOnGoBannerLink"
        >
          <NovaImageContainer
            :image-url="banner.bannerImageUrl"
            :empty-type="'default'"
            :ratio="'auto'"
            class="desktop"
          />

          <NovaImageContainer
            :image-url="banner.bannerMobileImageUrl"
            :empty-type="'default'"
            :ratio="'auto'"
            class="mobile"
          />
        </div>

        <div class="calendar-wrap">
          <NovaCalendar
            :fill-parent="true"
            :checked-in-dates="attendanceStore.dailyCheck?.checkedInDates || []"
            :init-day="initDay"
            :loading="dailCheckListLoading"
            @update:day="handleOnUpdateDay"
          />
        </div>
      </div>

      <div class="section-row">
        <div class="counter-wrap">
          <Swiper v-bind="swiperOptions">
            <SwiperSlide>
              <div class="counter-slide">
                <span>{{ t('dailyCheck.totalCheckinDays') }}</span>
                <span>
                  {{
                    attendanceStore.dailyCheck?.totalAttendanceDateCount || 0
                  }}
                  {{ t('units.days') }}
                </span>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div class="counter-slide">
                <span>{{ t('dailyCheck.consecutiveCheckinDays') }}</span>
                <span>
                  {{
                    attendanceStore.dailyCheck
                      ?.consecutiveAttendanceDateCount || 0
                  }}
                  {{ t('units.days') }}
                </span>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <div class="action-wrap">
          <button
            type="button"
            :class="[
              'daily-check-button',
              {
                on: attendanceStore.dailyCheck.hasAttendanceToday,
                loading: dailyCheckLoading,
              },
            ]"
            @click="handleOnDailyCheck"
          >
            <span class="inner">
              <NovaIcon
                :icon="{ type: 'outline', name: 'check-circle' }"
                :size="28"
                class="icon"
              />
              <span class="label">
                {{
                  t(
                    `dailyCheck.${
                      attendanceStore.dailyCheck.hasAttendanceToday
                        ? 'checkedIn'
                        : 'check'
                    }`
                  )
                }}
              </span>
            </span>

            <NovaLoadingIndicator
              v-if="dailyCheckLoading"
              :overlay="true"
              :bg-bright="'light'"
            />
          </button>

          <div class="toggle-daily-check-notification">
            <span class="daily-check-notification">
              {{ t('dailyCheck.notification') }}
            </span>
            <NovaSwitch
              :model-value="attendanceStore.dailyCheck.notification"
              :loading="dailyCheckNotificationLoading"
              @update="handleOnUpdateDailyCheckNotification"
            />
          </div>
        </div>
      </div>

      <div class="guide-wrap">
        <div class="guide-header">
          {{ t('dailyCheck.guide') }}
        </div>
        <div class="guide-body">
          <p v-for="(item, index) in guides" :key="index" class="guide-item">
            {{ item }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <NovaBoxError v-else :error="err" :instance="true" />
</template>

<style lang="scss" scoped>
.page-daily-check {
  padding: 18px 0;

  @include mobile {
    padding: 6.7px 15px 80px;
  }

  .page-title-wrap {
    display: flex;
    align-items: center;
    height: 65px;
    width: 100%;
    padding: 0 30px;
    border-radius: 13px;
    background-color: $color-white;

    @include mobile {
      height: 50px;
      border-radius: 10px;
      padding: 0 18px;
    }

    .page-title {
      font-size: 30px;
      font-weight: 600;
      color: $color-black;

      @include mobile {
        font-size: 19.54px;
        font-weight: 700;
        line-height: 23.65px;
        letter-spacing: -5%;
      }
    }
  }

  .page-contents-wrap {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    @include mobile {
      margin-top: 8px;
      gap: 10px;
    }

    .section-row {
      display: flex;
      flex-direction: row;
      gap: 24px;

      @include tablet {
        flex-direction: column;
      }

      @include mobile {
        flex-direction: column;
        gap: 8px;
      }
    }

    .banner-wrap {
      flex-shrink: 0;
      width: 520px;
      height: 520px;
      overflow: hidden;
      border-radius: 8.44px;
      background-color: $color-bg-custom-2;

      @include tablet {
        width: 100%;
        height: auto;
      }

      &.link {
        cursor: pointer;
      }

      .desktop {
        display: block;

        @include mobile {
          display: none;
        }
      }

      .mobile {
        display: none;

        @include mobile {
          display: block;
        }
      }
    }

    .calendar-wrap {
      flex-grow: 1;
      overflow: hidden;
      border-radius: 8.44px;
    }

    .counter-wrap {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 520px;
      height: 78px;
      background-color: $color-white;
      border-radius: 10px;

      @include tablet {
        width: 100%;
      }

      @include mobile {
        width: 100%;
        height: 58.5px;
      }

      :deep(.swiper) {
        height: 100%;
        width: 100%;
      }

      :deep(.swiper-pagination) {
        bottom: 0;
      }

      @include mobile {
        :deep(.swiper-button-prev) {
          left: 0;
        }
        :deep(.swiper-button-next) {
          right: 0;
        }
      }
    }

    .action-wrap {
      flex-grow: 1;
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 78px;
      padding: 0 20px 0 24px;
      background-color: $color-white;
      border-radius: 10px;

      @include mobile {
        height: 81px;
        padding: 0 12px;
      }

      .toggle-daily-check-notification {
        display: flex;
        align-items: center;
        gap: 12px;

        @include mobile {
          gap: 4px;
        }

        .daily-check-notification {
          font-size: 14.06px;
          line-height: 16.88px;
          letter-spacing: 0.33px;

          @include mobile {
            font-size: 10px;
            font-weight: 500;
            line-height: 14.4px;
            letter-spacing: 0.2px;
          }
        }
      }
    }

    .guide-wrap {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 0 15px;
      background-color: $color-white;
      border-radius: $radius-10;

      .guide-header {
        display: flex;
        align-items: center;
        width: 100%;
        height: 60px;
        padding: 0 15px;
        font-size: 20px;
        font-weight: 700;
        color: $color-black;
        border-bottom: 1px solid $color-bg-custom-3;

        @include mobile {
          height: 72px;
          text-align: center;
        }
      }

      .guide-body {
        padding: 15px;

        > p.guide-item {
          position: relative;
          padding-left: 16px;
          font-size: 14.06px;
          line-height: 40px;
          letter-spacing: -3.5%;

          &:before {
            position: absolute;
            top: 0;
            left: 0;
            content: '•';
            height: 40px;
          }

          @include mobile {
            line-height: 1.4;
          }
        }
      }
    }
  }

  .counter-slide {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 100%;
    padding: 0 41px 0 60px;
    font-size: 16px;
    font-weight: 500;
    line-height: 36px;
    letter-spacing: 0.59px;
    color: #6b7897;

    @include mobile {
      padding: 0 30px;
      font-size: 10px;
      line-height: 24px;
      letter-spacing: 0.45px;
    }
  }

  .daily-check-button {
    position: relative;
    display: flex;
    align-items: center;
    height: 52.85px;
    border: 5px solid transparent;
    border-radius: 10.07px;
    background-image: linear-gradient(#fff, #fff),
      linear-gradient(180deg, #88cdfa 0%, #5b7bfd 100%);
    background-origin: border-box;
    background-clip: content-box, border-box;

    @include mobile {
      height: 48.54px;
    }

    &.on {
      background-image: linear-gradient(180deg, #88cdfa 0%, #5b7bfd 100%);

      > span.inner {
        > .icon {
          color: $color-white;
        }

        > .label {
          color: $color-white;
          font-weight: 600;
          text-shadow: 0 2px 2px hex-to-rgba($color-black, 0.25);
        }
      }
    }

    &.loading {
      > span.inner {
        > .icon,
        > .label {
          opacity: 0.2;
        }
      }
    }

    > span.inner {
      display: flex;
      align-items: center;
      gap: 11.77px;
      justify-content: center;
      padding: 0 36.92px 0 22.05px;

      @include mobile {
        padding: 0 10px;
        gap: 4px;
      }

      > .icon {
        color: #5f82fc;
        transition: opacity 0.2s ease-in-out;
      }

      > .label {
        font-size: 20.97px;
        color: $color-black;
        transition: opacity 0.2s ease-in-out;

        @include mobile {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
